import React, { useState, useEffect, useMemo } from "react";
import { Calendar, Clock, User, MapPin, Phone, Video, Plus, Search, Filter, Download, Eye, Edit, Trash2, X, Check, AlertCircle, ExternalLink } from "lucide-react";
import { jobsEndpoint } from "../constants";
import { profilesEndpoint } from "../constants";

// API configuration
const API_BASE = jobsEndpoint;
const token = localStorage.getItem('token');

const apiRequest = async (endpoint, options = {}) => {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Token ${token}`,
      ...options.headers,
    },
    ...options,
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'API request failed');
  }
  
  return response.json();
};

// Interview API functions
const interviewAPI = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`my-interviews/?${query}`);
  },
  get: (id) => apiRequest(`interviews/${id}/`),
  create: (data) => apiRequest('interviews/schedule/', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiRequest(`interviews/${id}/reschedule/`, { method: 'PATCH', body: JSON.stringify(data) }),
  cancel: (id, reason) => apiRequest(`interviews/${id}/cancel/`, { method: 'PATCH', body: JSON.stringify({ reason }) }),
  complete: (id, data) => apiRequest(`interviews/${id}/complete/`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id) => apiRequest(`interviews/${id}/`, { method: 'DELETE' }),
  getUserInterviews: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`interviews/user/?${query}`);
  }
};

// Google Meet API functions
const meetingAPI = {
  create: (data) => apiRequest('meetings/create/', { method: 'POST', body: JSON.stringify(data) }),
  get: (meetingId) => apiRequest(`meetings/${meetingId}/`, { method: 'GET' }),
  end: (meetingId) => apiRequest(`meetings/${meetingId}/end/`, { method: 'POST' })
};

const InterviewGoogle = () => {
  const [currentView, setCurrentView] = useState('list');
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInterview, setSelectedInterview] = useState(null);

  // Fetch interviews
  const fetchInterviews = async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const data = await interviewAPI.list(params);
      console.log(data)
      setInterviews(data.results || []);
    } catch (err) {
      setError(err.message);
      // Mock data for demonstration
      setInterviews([
        {
          id: 1,
          job: { id: 1, title: "Frontend Developer", company: "Tech Corp" },
          interviewer: { id: 1, first_name: "John", last_name: "Doe", email: "john@example.com" },
          interviewee: { id: 2, first_name: "Jane", last_name: "Smith", email: "jane@example.com" },
          interview_date: "2024-06-15T10:00:00Z",
          is_active: true,
          is_upcoming: true,
          date_published: "2024-06-10T08:00:00Z",
          meet_url: null,
          meeting_id: null
        },
        {
          id: 2,
          job: { id: 2, title: "Backend Engineer", company: "StartupXYZ" },
          interviewer: { id: 1, first_name: "John", last_name: "Doe", email: "john@example.com" },
          interviewee: { id: 3, first_name: "Bob", last_name: "Wilson", email: "bob@example.com" },
          interview_date: "2024-06-14T14:00:00Z",
          is_active: true,
          is_upcoming: false,
          date_published: "2024-06-08T09:00:00Z",
          meet_url: "https://meet.google.com/abc-def-ghi",
          meeting_id: "spaces/abc123"
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterviews();
  }, []);

  // Filter interviews
  const filteredInterviews = useMemo(() => {
    let result = interviews;

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(interview => 
        interview.job?.title?.toLowerCase().includes(search) ||
        interview.interviewer?.first_name?.toLowerCase().includes(search) ||
        interview.interviewer?.last_name?.toLowerCase().includes(search) ||
        interview.interviewee?.first_name?.toLowerCase().includes(search) ||
        interview.interviewee?.last_name?.toLowerCase().includes(search)
      );
    }

    if (statusFilter !== 'all') {
      switch (statusFilter) {
        case 'upcoming':
          result = result.filter(i => i.is_upcoming && i.is_active);
          break;
        case 'completed':
          result = result.filter(i => !i.is_upcoming && i.is_active);
          break;
        case 'cancelled':
          result = result.filter(i => !i.is_active);
          break;
      }
    }

    return result;
  }, [interviews, searchTerm, statusFilter]);

  const getStatusBadge = (interview) => {
    if (!interview.is_active) {
      return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Cancelled</span>;
    }
    if (interview.is_upcoming) {
      return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">Upcoming</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Completed</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';
    return new Date(dateString).toLocaleString();
  };

  // Schedule Interview Component with Google Meet Integration
  const ScheduleInterview = ({ onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
      job: '',
      interviewer: '',
      interviewee: '',
      interview_date: '',
      notes: '',
      // Google Meet specific fields
      title: '',
      description: '',
      participants: []
    });
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [meetingDetails, setMeetingDetails] = useState(null);
    
    // Data states
    const [jobs, setJobs] = useState([]);
    const [applicants, setApplicants] = useState([]);
    const [interviewers, setInterviewers] = useState([]);

    // Load jobs and interviewers on component mount
    useEffect(() => {
      const fetchInitialData = async () => {
        setLoading(true);
        try {
          // Fetch jobs
          const jobsResponse = await fetch(jobsEndpoint + "list", {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
          });
          
          if (jobsResponse.ok) {
            const jobsData = await jobsResponse.json();
            setJobs(jobsData.results || jobsData);
          } else {
            console.error('Failed to fetch jobs');
          }

          // Fetch interviewers (profiles)
          const interviewersResponse = await fetch(profilesEndpoint+'profile_list', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Token ${token}`
            }
          });
          
          if (interviewersResponse.ok) {
            const interviewersData = await interviewersResponse.json();
            setInterviewers(interviewersData.results || interviewersData);
          } else {
            console.error('Failed to fetch interviewers');
          }

        } catch (error) {
          console.error('Error fetching initial data:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchInitialData();
    }, []);

    // Load applicants when job is selected
    useEffect(() => {
      const fetchApplicants = async () => {
        if (!formData.job) {
          setApplicants([]);
          return;
        }

        setLoading(true);
        try {
          const response = await fetch(jobsEndpoint + `${formData.job}/applicant_list`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Token ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            setApplicants(data.applicants || []);
            
            // Reset interviewee selection when job changes
            setFormData(prev => ({ ...prev, interviewee: '' }));
          } else {
            console.error('Failed to fetch applicants');
            setApplicants([]);
          }
        } catch (error) {
          console.error('Error fetching applicants:', error);
          setApplicants([]);
        } finally {
          setLoading(false);
        }
      };

      fetchApplicants();
    }, [formData.job]);

    // Update participants list when interviewer or interviewee changes
    useEffect(() => {
      const participants = [];
      
      // Add interviewer email
      const interviewer = interviewers.find(i => i.id === parseInt(formData.interviewer));
      if (interviewer && interviewer.email) {
        participants.push(interviewer.email);
      }
      
      // Add interviewee email (from applicant data)
      const applicant = applicants.find(a => a.user === parseInt(formData.interviewee));
      if (applicant && applicant.email) {
        participants.push(applicant.email);
      }
      
      setFormData(prev => ({
        ...prev,
        participants: participants
      }));
    }, [formData.interviewer, formData.interviewee, interviewers, applicants]);

    // Auto-generate meeting title when job is selected
    useEffect(() => {
      if (formData.job) {
        const job = jobs.find(j => j.id === parseInt(formData.job));
        if (job) {
          setFormData(prev => ({
            ...prev,
            title: `Interview for ${job.title} position`,
            description: `Interview session for the ${job.title} position at ${job.company || 'the company'}`
          }));
        }
      }
    }, [formData.job, jobs]);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);
      setFormError(null);
      setMeetingDetails(null);

      try {
        // First, create the Google Meet meeting
        const meetingPayload = {
          title: formData.title || `Interview for ${jobs.find(j => j.id === parseInt(formData.job))?.title || 'a job'}`,
          description: formData.description || 'Interview session',
          participants: formData.participants
        };

        console.log('Creating meeting with payload:', meetingPayload);
        const meetingResponse = await meetingAPI.create(meetingPayload);
        console.log('Meeting created:', meetingResponse);
        
        setMeetingDetails(meetingResponse);

        // Then, schedule the interview in your system with the meeting details
        const interviewPayload = {
          ...formData,
          meet_url: meetingResponse.meet_url,
          meeting_id: meetingResponse.meeting_id,
          space_name: meetingResponse.space_name
        };

        const interviewResponse = await interviewAPI.create(interviewPayload);
        console.log('Interview scheduled:', interviewResponse);

        onSuccess();
        // Keep modal open to show meeting details
      } catch (err) {
        console.error('Error:', err);
        setFormError(err.message);
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Schedule Interview with Google Meet</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={20} />
            </button>
          </div>

          {formError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {formError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job Position</label>
              <select
                value={formData.job}
                onChange={(e) => setFormData({...formData, job: e.target.value})}
                required
                disabled={loading}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">Select a job</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>
                    {job.title} - {job.company}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Interviewer</label>
              <select
                value={formData.interviewer}
                onChange={(e) => setFormData({...formData, interviewer: e.target.value})}
                required
                disabled={loading}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">Select interviewer</option>
                {interviewers.map(profile => (
                  <option key={profile.id} value={profile.id}>
                    {profile.first_name} {profile.last_name} ({profile.email})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Interviewee</label>
              <select
                value={formData.interviewee}
                onChange={(e) => setFormData({...formData, interviewee: e.target.value})}
                required
                disabled={!formData.job || loading}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">
                  {!formData.job 
                    ? "Select a job first" 
                    : applicants.length === 0 
                      ? "No applicants found" 
                      : "Select interviewee"}
                </option>
                {applicants.map(applicant => (
                  <option key={applicant.id} value={applicant.user}>
                    Applicant #{applicant.id} - {applicant.job_title}
                    {applicant.application_status && ` (${applicant.application_status})`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Interview Date & Time</label>
              <input
                type="datetime-local"
                value={formData.interview_date}
                onChange={(e) => setFormData({...formData, interview_date: e.target.value})}
                required
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Interview with John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={2}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Brief description of the interview..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Participants</label>
              <div className="p-2 bg-gray-50 rounded-md min-h-[40px]">
                {formData.participants.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {formData.participants.map((email, index) => (
                      <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                        {email}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-gray-500 text-sm">Select interviewer and interviewee to auto-populate</span>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Add any additional notes for the interview..."
              />
            </div>

            {meetingDetails && (
              <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
                <p className="font-semibold flex items-center">
                  <Check size={16} className="mr-2" />
                  Google Meet Created Successfully!
                </p>
                <div className="mt-2 space-y-2">
                  <p className="text-sm">
                    <strong>Meeting URL:</strong>{' '}
                    <a 
                      href={meetingDetails.meet_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-blue-700 underline hover:text-blue-900 inline-flex items-center"
                    >
                      Join Meeting <ExternalLink size={12} className="ml-1" />
                    </a>
                  </p>
                  <p className="text-sm">
                    <strong>Meeting ID:</strong> {meetingDetails.meeting_id}
                  </p>
                  <p className="text-sm">
                    <strong>Organizer:</strong> {meetingDetails.organizer}
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                {meetingDetails ? 'Close' : 'Cancel'}
              </button>
              {!meetingDetails && (
                <button
                  type="submit"
                  disabled={submitting || loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Video size={16} />
                      Schedule with Google Meet
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Interview Details Component with Google Meet integration
  const InterviewDetails = ({ interview, onClose, onUpdate }) => {
    const [editing, setEditing] = useState(false);
    const [formData, setFormData] = useState({
      interviewer: interview.interviewer?.id || '',
      interviewee: interview.interviewee?.id || '',
      interview_date: interview.interview_date || '',
      notes: interview.notes || ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);

    const handleUpdate = async (e) => {
      e.preventDefault();
      setSubmitting(true);
      setFormError(null);

      try {
        await interviewAPI.update(interview.id, formData);
        onUpdate();
        setEditing(false);
      } catch (err) {
        setFormError(err.message);
      } finally {
        setSubmitting(false);
      }
    };

    const handleCancel = async () => {
      const reason = prompt('Please provide a reason for cancellation:');
      if (reason) {
        try {
          await interviewAPI.cancel(interview.id, reason);
          onUpdate();
        } catch (err) {
          alert('Failed to cancel interview: ' + err.message);
        }
      }
    };

    const handleComplete = async () => {
      const feedback = prompt('Please provide feedback (optional):');
      try {
        await interviewAPI.complete(interview.id, { feedback });
        onUpdate();
      } catch (err) {
        alert('Failed to mark as completed: ' + err.message);
      }
    };

    const handleEndMeeting = async () => {
      if (!interview.meeting_id) {
        alert('No Google Meet meeting associated with this interview');
        return;
      }

      if (window.confirm('Are you sure you want to end the Google Meet meeting?')) {
        try {
          await meetingAPI.end(interview.meeting_id);
          alert('Google Meet meeting ended successfully');
          onUpdate();
        } catch (err) {
          alert('Failed to end meeting: ' + err.message);
        }
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Interview Details</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={20} />
            </button>
          </div>

          {formError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {formError}
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">{interview.job?.title}</h3>
              {getStatusBadge(interview)}
            </div>

            {/* Google Meet section */}
            {interview.meet_url && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 flex items-center mb-2">
                  <Video size={16} className="mr-2" />
                  Google Meet Details
                </h4>
                <div className="space-y-2">
                  <p className="text-sm">
                    <strong>Meeting URL:</strong>{' '}
                    <a 
                      href={interview.meet_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-blue-700 underline hover:text-blue-900 inline-flex items-center"
                    >
                      Join Meeting <ExternalLink size={12} className="ml-1" />
                    </a>
                  </p>
                  {interview.meeting_id && (
                    <p className="text-sm">
                      <strong>Meeting ID:</strong> {interview.meeting_id}
                    </p>
                  )}
                </div>
              </div>
            )}

            {editing ? (
              <form onSubmit={handleUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Interview Date & Time</label>
                  <input
                    type="datetime-local"
                    value={formData.interview_date}
                    onChange={(e) => setFormData({...formData, interview_date: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    rows={3}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {submitting ? 'Updating...' : 'Update Interview'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Interviewer</label>
                    <p className="text-sm text-gray-900">
                      {interview.interviewer ? 
                        `${interview.interviewer.first_name} ${interview.interviewer.last_name}` : 
                        'Not assigned'
                      }
                    </p>
                    <p className="text-xs text-gray-500">{interview.interviewer?.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Interviewee</label>
                    <p className="text-sm text-gray-900">
                      {interview.interviewee ? 
                        `${interview.interviewee.first_name} ${interview.interviewee.last_name}` : 
                        'Not assigned'
                      }
                    </p>
                    <p className="text-xs text-gray-500">{interview.interviewee?.email}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500">Scheduled Date & Time</label>
                  <p className="text-sm text-gray-900">{formatDate(interview.interview_date)}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500">Job Position</label>
                  <p className="text-sm text-gray-900">{interview.job?.title} - {interview.job?.company}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500">Created</label>
                  <p className="text-sm text-gray-900">{formatDate(interview.date_published)}</p>
                </div>

                {interview.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Notes</label>
                    <p className="text-sm text-gray-900">{interview.notes}</p>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  {interview.is_active && (
                    <>
                      <button
                        onClick={() => setEditing(true)}
                        className="px-4 py-2 text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200"
                      >
                        <Edit size={16} className="inline mr-1" />
                        Edit
                      </button>
                      
                      {interview.meet_url && interview.meeting_id && (
                        <button
                          onClick={handleEndMeeting}
                          className="px-4 py-2 text-orange-600 bg-orange-100 rounded-md hover:bg-orange-200"
                        >
                          <Video size={16} className="inline mr-1" />
                          End Meet
                        </button>
                      )}
                      
                      {interview.is_upcoming && (
                        <button
                          onClick={handleComplete}
                          className="px-4 py-2 text-green-600 bg-green-100 rounded-md hover:bg-green-200"
                        >
                          <Check size={16} className="inline mr-1" />
                          Mark Complete
                        </button>
                      )}
                      
                      <button
                        onClick={handleCancel}
                        className="px-4 py-2 text-red-600 bg-red-100 rounded-md hover:bg-red-200"
                      >
                        <X size={16} className="inline mr-1" />
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Interview Management</h1>
            <p className="text-gray-600">Schedule and manage interviews with Google Meet integration</p>
          </div>
          <button
            onClick={() => setCurrentView('schedule')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus size={20} />
            Schedule Interview
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex gap-4 items-center">
          <div className="relative flex-1">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search interviews..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="upcoming">Upcoming</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
          <AlertCircle size={20} className="mr-2" />
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading interviews...</p>
        </div>
      )}

      {/* Interviews Table */}
      {!loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Interviews ({filteredInterviews.length})
            </h2>
          </div>

          {filteredInterviews.length === 0 ? (
            <div className="text-center py-12">
              <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No interviews found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filters'
                  : 'Get started by scheduling your first interview'
                }
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <button
                  onClick={() => setCurrentView('schedule')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Schedule Interview
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Job Position
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Interviewer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Interviewee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Google Meet
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInterviews.map((interview) => (
                    <tr key={interview.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {interview.job?.title || 'No job assigned'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {interview.job?.company}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                              <User size={16} className="text-gray-600" />
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {interview.interviewer ? 
                                `${interview.interviewer.first_name} ${interview.interviewer.last_name}` : 
                                'Not assigned'
                              }
                            </div>
                            <div className="text-sm text-gray-500">
                              {interview.interviewer?.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <User size={16} className="text-blue-600" />
                            </div>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {interview.interviewee ? 
                                `${interview.interviewee.first_name} ${interview.interviewee.last_name}` : 
                                'Not assigned'
                              }
                            </div>
                            <div className="text-sm text-gray-500">
                              {interview.interviewee?.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <Clock size={16} className="mr-2 text-gray-400" />
                          {formatDate(interview.interview_date)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(interview)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {interview.meet_url ? (
                          <a
                            href={interview.meet_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center text-sm text-blue-600 hover:text-blue-900"
                          >
                            <Video size={16} className="mr-1" />
                            Join Meeting
                            <ExternalLink size={12} className="ml-1" />
                          </a>
                        ) : (
                          <span className="text-sm text-gray-500">No meeting</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => setSelectedInterview(interview)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          <Eye size={16} className="inline mr-1" />
                          View
                        </button>
                        
                        {interview.is_active && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedInterview(interview);
                                setCurrentView('edit');
                              }}
                              className="text-green-600 hover:text-green-900 mr-4"
                            >
                              <Edit size={16} className="inline mr-1" />
                              Edit
                            </button>
                            
                            <button
                              onClick={async () => {
                                if (window.confirm('Are you sure you want to cancel this interview?')) {
                                  const reason = prompt('Please provide a reason for cancellation:');
                                  if (reason) {
                                    try {
                                      await interviewAPI.cancel(interview.id, reason);
                                      fetchInterviews();
                                    } catch (err) {
                                      setError('Failed to cancel interview: ' + err.message);
                                    }
                                  }
                                }
                              }}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 size={16} className="inline mr-1" />
                              Cancel
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal Components */}
      {currentView === 'schedule' && (
        <ScheduleInterview
          onClose={() => setCurrentView('list')}
          onSuccess={() => {
            fetchInterviews();
            setCurrentView('list');
          }}
        />
      )}

      {selectedInterview && currentView === 'list' && (
        <InterviewDetails
          interview={selectedInterview}
          onClose={() => setSelectedInterview(null)}
          onUpdate={() => {
            fetchInterviews();
            setSelectedInterview(null);
          }}
        />
      )}
    </div>
  );
};

export default InterviewGoogle;